import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Trash2, Copy, ArrowLeft, RotateCcw, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import SuperAdminCategoryTabs from "../components/SuperAdminCategoryTabs";

export default function PassportNumbersPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState("RO");
  const [statusFilter, setStatusFilter] = useState("");
  const [count, setCount] = useState(1);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/reference/countries");
      setCountries(data);
    })();
  }, []);

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (country) params.set("country", country);
      if (statusFilter) params.set("status", statusFilter);
      const { data } = await api.get(`/superadmin/passport-numbers?${params}`);
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [country, statusFilter]);

  const generate = async () => {
    setBusy(true);
    try {
      await api.post("/superadmin/passport-numbers/generate", { country, count: parseInt(count, 10) });
      toast.success(t("passports.generatedToast", { count }));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(t("passports.confirmDelete", { code: p.full_code }))) return;
    try {
      await api.delete(`/superadmin/passport-numbers/${p.id}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const resetAssignment = async (p) => {
    const en = i18n.language !== "ro";
    const msg = en
      ? `Free up passport ${p.full_code}? It will be detached from the current member and put back into the reserved pool.`
      : `Eliberezi pașaportul ${p.full_code}? Va fi detașat de la membrul curent și redus la statusul "rezervat".`;
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/superadmin/passport-numbers/${p.id}/reset`);
      toast.success(t("passportNumbers.passport_reset_to_reserved"));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const runSearch = async () => {
    if (!searchQ.trim()) {
      setSearchResults([]);
      return;
    }
    try {
      const { data } = await api.get(`/superadmin/passport-numbers/search?q=${encodeURIComponent(searchQ)}`);
      setSearchResults(data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(t("passports.copied"));
    } catch { /* ignore */ }
  };

  const counters = useMemo(() => {
    const reserved = items.filter((i) => i.status === "reserved").length;
    const assigned = items.filter((i) => i.status === "assigned").length;
    return { total: items.length, reserved, assigned };
  }, [items]);

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-10" data-testid="passport-numbers-page">
      <div>
        <h1 className="page-title">{t("passports.title")}</h1>
        <p className="page-subtitle">{t("passports.subtitle")}</p>
      </div>
      <div className="gold-divider my-6" />

      <SuperAdminCategoryTabs active="passports" />

      <button className="btn-outline-gold mb-4 flex items-center gap-1" onClick={() => navigate("/superadmin")}>
        <ArrowLeft size={12} /> Super-Admin
      </button>

      <div className="panel mb-6">
        <div className="panel-head">{t("passports.generateHeading")}</div>
        <div className="panel-body">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
            <div>
              <label className="label-lodge">{t("passports.country")}</label>
              <select
                className="input-lodge"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                data-testid="passport-country-select"
              >
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} · {i18n.language !== "ro" ? c.name_en : c.name_ro}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-lodge">{t("passports.count")}</label>
              <input
                type="number"
                min={1}
                max={1000}
                className="input-lodge"
                value={count}
                onChange={(e) => setCount(e.target.value)}
                data-testid="passport-count-input"
              />
            </div>
            <button
              type="button"
              className="btn-primary-gold flex items-center gap-1"
              disabled={busy}
              onClick={generate}
              data-testid="generate-passports-btn"
            >
              <Plus size={14} /> {busy ? "..." : t("passports.generateBtn")}
            </button>
          </div>
        </div>
      </div>

      <div className="panel mb-6" data-testid="passport-search-panel">
        <div className="panel-head">
          {t("passportNumbers.search_reset_assigned_passports")}
        </div>
        <div className="panel-body">
          <div className="flex flex-wrap gap-2 items-end">
            <div style={{ flex: 1, minWidth: 220 }}>
              <label className="label-lodge">
                {t("passportNumbers.code_full_or_partial")}
              </label>
              <input
                type="text"
                className="input-lodge"
                placeholder={t("passportNumbers.e_g_ro_0001")}
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
                data-testid="passport-search-input"
              />
            </div>
            <button className="btn-primary-gold flex items-center gap-1" onClick={runSearch} data-testid="passport-search-btn">
              <Search size={12} /> {t("passportNumbers.search")}
            </button>
          </div>
          {searchResults.length > 0 && (
            <div className="mt-4" style={{ maxHeight: 220, overflowY: "auto" }}>
              {searchResults.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between p-2"
                  style={{ borderBottom: "1px solid #ead8a8" }}
                  data-testid={`passport-search-result-${p.id}`}
                >
                  <div>
                    <span style={{ fontFamily: "Cinzel, serif", fontWeight: 600, color: "#0d1b2a" }}>{p.full_code}</span>
                    <span className="kv-label" style={{ marginLeft: 12 }}>{p.status}</span>
                    {p.assigned_to_member_id && (
                      <span className="kv-label" style={{ marginLeft: 12 }}>→ member {p.assigned_to_member_id.slice(0, 8)}</span>
                    )}
                  </div>
                  {p.status === "assigned" && (
                    <button
                      className="btn-outline-gold flex items-center gap-1"
                      onClick={() => resetAssignment(p)}
                      data-testid={`reset-search-passport-${p.id}`}
                    >
                      <RotateCcw size={11} /> {t("passportNumbers.reset")}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="kv-label">
          {t("passports.total")} <strong>{counters.total}</strong> · {t("passports.reserved")}{" "}
          <strong>{counters.reserved}</strong> · {t("passports.assigned")} <strong>{counters.assigned}</strong>
        </div>
        <select
          className="input-lodge"
          style={{ maxWidth: 180 }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="passport-status-filter"
        >
          <option value="">{t("passports.allStatuses")}</option>
          <option value="reserved">{t("passports.reserved")}</option>
          <option value="assigned">{t("passports.assigned")}</option>
        </select>
      </div>

      <table className="lodge-table" data-testid="passports-table">
        <thead>
          <tr>
            <th>{t("passports.code")}</th>
            <th>{t("passports.country")}</th>
            <th>Status</th>
            <th>{t("passports.assignedTo", "Atribuit")}</th>
            <th>{t("users.createdAt")}</th>
            <th style={{ textAlign: "right" }}>{t("common.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={6} style={{ textAlign: "center", fontStyle: "italic", color: "#858481", padding: "2rem" }}>
                {t("passports.empty")}
              </td>
            </tr>
          ) : (
            items.map((p) => (
              <tr key={p.id} data-testid={`passport-${p.id}`}>
                <td style={{ fontFamily: "Cinzel, serif", color: "#0d1b2a", fontWeight: 600 }}>{p.full_code}</td>
                <td>{p.country}</td>
                <td>
                  <span
                    className="badge-status"
                    style={{
                      color: p.status === "assigned" ? "#7c2a2a" : "#3e6a3c",
                      background: p.status === "assigned" ? "#f6e0e0" : "#e8f0e5",
                      border: `1px solid ${p.status === "assigned" ? "#d9b9b9" : "#b9cfb4"}`,
                      fontSize: "0.62rem",
                    }}
                  >
                    {t(`passports.${p.status}`)}
                  </span>
                </td>
                <td className="kv-label">{p.assigned_to_member_id ? p.assigned_to_member_id.slice(0, 8) : "—"}</td>
                <td className="kv-label">{new Date(p.generated_at).toLocaleDateString()}</td>
                <td style={{ textAlign: "right" }}>
                  <div className="flex justify-end gap-1">
                    <button className="btn-outline-gold" onClick={() => copyCode(p.full_code)} title="Copy">
                      <Copy size={11} />
                    </button>
                    {p.status === "reserved" && (
                      <button className="btn-danger" onClick={() => remove(p)} data-testid={`delete-passport-${p.id}`}>
                        <Trash2 size={11} />
                      </button>
                    )}
                    {p.status === "assigned" && (
                      <button
                        className="btn-outline-gold"
                        onClick={() => resetAssignment(p)}
                        title={t("passportNumbers.free_up_reset_to_reserved")}
                        data-testid={`reset-passport-${p.id}`}
                      >
                        <RotateCcw size={11} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
